import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Ams } from 'src/app/amsconfig';
import {
  IUserColumnChoice,
  RebundleResult,
  IAddJobItemRequest,
  ICreateOrderRequest,
  IJobItem,
  IStrategy,
  IProductionEvent,
} from 'src/app/core/dto';
import { BundleRules } from './store/order/selectors';

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  private http = inject(HttpClient);

  private get baseUrl(): string {
    return Ams?.Config?.BASE_URL || '';
  }

  getOrderSettingColumns() {
    return this.http.get<IUserColumnChoice[]>(
      `${this.baseUrl}/_api/user/settings/ordersColumns`
    );
  }

  updateOrderSettingColumns(data: IUserColumnChoice[]) {
    return this.http.post<IUserColumnChoice[]>(
      `${this.baseUrl}/_api/user/settings/ordersColumns`,
      {
        data,
      }
    );
  }

  getOrderItemColumns() {
    return this.http.get<IUserColumnChoice[]>(
      `${this.baseUrl}/_api/user/settings/orderItemsColumns`
    );
  }

  updateOrderItemColumns(data: IUserColumnChoice[]) {
    return this.http.post(`${this.baseUrl}/_api/user/settings/orderItemsColumns`, { data });
  }

  getJobPrintTemplates() {
    return this.http.get<string[]>(`${this.baseUrl}/api/job/printTemplates`);
  }

  updatePreferencesSettings(data: IUserColumnChoice[]) {
    // todo: this is not being used yet. it's currently just an example.
    return this.http.post<any>(
      `${this.baseUrl}/_api/user/settings/preferences/orders-list.columns`,
      {
        value: JSON.stringify(data),
      }
    );
  }

  onSetHold(ordIds: number[] | number, hold: boolean) {
    return this.http.post(`${this.baseUrl}/api/orders/sethold`, null, {
      params: {
        ordIds,
        hold,
      },
    });
  }

  deleteJob(ordId: number) {
    return this.http.delete(Ams.Config.BASE_URL + `/api/job/${ordId}`);
  }

  requeueSolutions(ordId: number) {
    return this.http.post<{ count: number; message: string }>(
      `${this.baseUrl}/api/ordercommand/${ordId}/requeue-solutions`,
      {}
    );
  }

  getBundleRules(ordId: number) {
    return this.http.get<BundleRules>(`${this.baseUrl}/api/job/${ordId}/bundlerules`);
  }

  printBundle(bundleCode: string) {
    return this.http.post<string>(`${this.baseUrl}/_api/bundle/${bundleCode}/print`, {});
  }

  rebundleItems(args: { items: IJobItem[]; itemIds: number[]; rules: BundleRules; ordId: number }) {
    return this.http.post<RebundleResult>(`${this.baseUrl}/api/ordercommand/rebundle`, args);
  }

  saveOrderDetail(ordId: number, operations: { value: string; path: string; op: string }[]) {
    return this.http.patch(`${this.baseUrl}/api/ordercommand/${ordId}/savechanges`, {
      operations,
    });
  }

  addItem(ordId: number, item: IAddJobItemRequest) {
    return this.http.post(`${this.baseUrl}/api/ordercommand/${ordId}/items`, item);
  }

  deleteItems(ordId: number, itemIds: number[]) {
    return this.http.post(`${this.baseUrl}/api/ordercommand/${ordId}/deleteitems`, { itemIds });
  }

  createOrder(request: ICreateOrderRequest) {
    return this.http.post<{ ordId: number | null }>(
      `${this.baseUrl}/api/ordercommand/create`,
      request
    );
  }

  resequence(ordId: number, items: IJobItem[], strategy: IStrategy) {
    return this.http.post<RebundleResult>(
      `${this.baseUrl}/api/ordercommand/rebundle/resequencebystrategy`,
      {
        ordId,
        items,
        strategy,
      }
    );
  }

  changeItemBundle(ordId: number, items: IJobItem[], itemIds: number[], targetBundle: number) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.post<RebundleResult>(
      `${this.baseUrl}/api/ordercommand/rebundle/combineitems`,
      {
        ordId,
        items,
        itemIds,
        targetBundle,
        bundleGroupField: 'none',
      },
      httpOptions
    );
  }

  resequenceItems(ordId: number, items: IJobItem[], itemIds: number[], targetSeqPos: number) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    return this.http.post<RebundleResult>(
      `${this.baseUrl}/api/ordercommand/rebundle/resequence`,
      {
        ordId,
        items,
        itemIds,
        targetSeqPos,
      },
      httpOptions
    );
  }

  saveNewBundles(ordId: number, items: IJobItem[], itemIds: number[], bundleGroupField: string) {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.post<RebundleResult>(
      `${this.baseUrl}/api/ordercommand/rebundle/combineitems`,
      {
        ordId,
        items,
        itemIds,
        targetBundle: -1, // means combine to new bundle
        bundleGroupField,
      },
      options
    );
  }
  saveDefonOrder(ordId: number, orderCode: string, materialCode: string, toolingCode: string) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    return this.http.post<{ ordId: number }>(
      `${this.baseUrl}/api/ordercommand/changedef`,
      {
        ordId,
        orderCode,
        materialCode,
        toolingCode,
      },
      httpOptions
    );
  }

  duplicateOrder(
    sourceOrdId: number,
    newOrderCode: string,
    materialCode: string,
    toolingCode: string
  ) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    return this.http.post<{ ordId: number }>(
      `${this.baseUrl}/api/ordercommand/duplicate`,
      {
        sourceOrdId,
        newOrderCode,
        materialCode,
        toolingCode,
      },
      httpOptions
    );
  }

  rebundleOrder(
    tabtoUrl: string,
    ordId: number,
    items: IJobItem[],
    itemIds: number[],
    targetWeight: number,
    targetItemCount: number,
    targetQty: number,
    moveQty: number,
    splitToNewBundles: boolean
  ) {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    return this.http.post<RebundleResult>(
      `${this.baseUrl}/api/ordercommand/rebundle/${tabtoUrl}`,
      {
        ordId,
        items,
        itemIds,
        targetWeight,
        targetItemCount,
        targetQty,
        moveQty,
        splitToNewBundles,
      },
      options
    );
  }

  deleteBulk(ordIds: number[]) {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.post<any>(
      `${this.baseUrl}/api/orderscommand/delete`,
      {
        ordIds,
      },
      options
    );
  }

  editBulk(ordIds: number[], bulkField: string, data: string) {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.patch<any>(
      `${this.baseUrl}/api/orderscommand/savechanges`,
      {
        ordIds,
        patch: {
          operations: [
            {
              path: `/job/${bulkField}`,
              value: data,
              op: 'replace',
            },
          ],
        },
      },
      options
    );
  }

  getProductionEvents(query: any) {
    let httpParams = new HttpParams({ fromObject: query });
    return this.http.get<IProductionEvent[]>(`${this.baseUrl}/_api/productionEvents`, {
      params: httpParams,
    });
  }
}
